import { useFormik } from "formik";
import { noop } from "lodash";
import { useEffect, useRef } from "react";
import { connect, type ConnectedProps } from "react-redux";

import Restricted from "../../../Application/Restricted";
import SettingsForm from "./SettingsForm";

import { RolePermissions } from "../../../../enums";
import { type IWizardStep } from "../../../../interfaces/IWizardStepsManager";
import { bindAction } from "../../../../interfaces/redux";
import { embeddedVideoSettings } from "../../../../utils/validationSchemas/embeddedVideosValidationSchema";
import { type AppDispatch, type RootState } from "../../../Application/globaltypes/redux";
import { saveSettings } from "../state/EmbeddedVideoDetailsSlice";
import { normalizedTagsSelector } from "../state/selectors";
import { updateEmbeddedVideoTags } from "../state/thunks/EmbeddedVideosEntityStateThunk";
import { fetchEmbeddedVideosTags } from "../state/thunks/EmbeddedVideosTagsThunk";

export type SettingsTabProps = IWizardStep & PropsFromRedux;

export const SettingsTab = (props: SettingsTabProps) => {
  const { fetchTags, updateTags, settings, normalizedTags, isLoading, acceptHandlers, entityId, disabled } = props;

  useEffect(() => {
    acceptHandlers?.({
      onPrevious: saveTags,
      onNext: saveTags,
    });
    fetchTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tagsFormik = useFormik({
    initialValues: settings,
    validationSchema: embeddedVideoSettings,
    onSubmit: noop,
    enableReinitialize: true,
  });

  const valuesRef = useRef(tagsFormik.values);
  useEffect(() => {
    valuesRef.current = tagsFormik.values;
  }, [tagsFormik.values]);

  const saveTags = () => {
    props.saveSettings(valuesRef.current);
  };

  return (
    <Restricted
      permissions={[RolePermissions.AssetsCreate]}
      renderContent={(hasPermission) => (
        <SettingsForm
          entityId={entityId}
          tagsFormik={tagsFormik}
          normalizedTags={normalizedTags}
          updateTags={updateTags}
          settings={settings}
          isDisabled={disabled || !hasPermission}
          isLoading={isLoading}
        />
      )}
    />
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const details = state.library.embeddedVideos.details;
  return {
    entityId: state.library.embeddedVideos.entitState.entityId,
    settings: details.embeddedVideo.settings,
    disabled: details.embeddedVideo.isPurchased,
    isLoading: details.isLoading,
    normalizedTags: normalizedTagsSelector(state),
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  fetchTags: bindAction(fetchEmbeddedVideosTags, dispatch),
  saveSettings: bindAction(saveSettings, dispatch),
  updateTags: bindAction(updateEmbeddedVideoTags, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(SettingsTab);
export default ConnectedComponent;
